import { useState, useEffect, useRef, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { supabase } from '../../../utils/supabase';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Shared import execution engine for the import modals (ImportCSVModal and
 * the admin Import Studio). Extracted from ImportCSVModal — owns everything
 * between "records are ready" and "results are in":
 *
 * - calls the importContacts/importDeals callable with the long client
 *   timeout (must exceed the 540s server limit),
 * - discovers the crm_import_history job for live progress polling,
 * - recovers from dropped connections by watching the job record instead of
 *   reporting a false failure (which invited duplicate re-uploads).
 *
 * `runImport` resolves with the results object (same shape the callable
 * returns; recovered results are rebuilt from the history row). It rejects
 * with the original error, augmented with `mayStillBeRunning` so callers can
 * warn against re-uploading.
 */
export default function useImportRunner() {
  const { currentUser } = useAuth();

  // Ref mirror of importJobId so async error handlers see the discovered job
  // even when React state hasn't re-rendered their closure.
  const importJobIdRef = useRef(null);
  const [importJobId, setImportJobId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  // Live progress: poll the import job row while the callable runs.
  useEffect(() => {
    if (!importing || !importJobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await supabase
          .from('crm_import_history')
          .select('imported_rows, updated_rows, failed_rows, total_rows, status')
          .eq('id', importJobId)
          .single();
        if (data && !cancelled) {
          const done = (data.imported_rows || 0) + (data.updated_rows || 0) + (data.failed_rows || 0);
          const pct = data.total_rows > 0 ? Math.round((done / data.total_rows) * 100) : 0;
          setImportProgress(pct);
          setProgressText(`${done} / ${data.total_rows} processed`);
        }
      } catch { /* ignore polling errors */ }
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [importing, importJobId]);

  const resetRunner = useCallback(() => {
    setImportJobId(null);
    importJobIdRef.current = null;
    setImporting(false);
    setImportProgress(0);
    setProgressText('');
  }, []);

  // Build a results object (same shape the callable returns) from the
  // crm_import_history row, for when the client connection drops but the
  // server finishes the import anyway.
  const buildResultsFromHistory = (job) => ({
    success: true,
    jobId: job.id,
    successCount: (job.imported_rows || 0) + (job.updated_rows || 0),
    insertedCount: job.imported_rows || 0,
    updatedCount: job.updated_rows || 0,
    errorCount: job.failed_rows || 0,
    warningCount: (job.errors?.warnings || []).length,
    duplicatesFound: job.duplicates_found || 0,
    errors: job.errors?.errors || [],
    warnings: job.errors?.warnings || [],
    summary: {
      totalLeads: job.total_rows || 0,
      successful: (job.imported_rows || 0) + (job.updated_rows || 0),
      inserted: job.imported_rows || 0,
      updated: job.updated_rows || 0,
      failed: job.failed_rows || 0,
      duplicates: job.duplicates_found || 0,
    },
  });

  // Poll crm_import_history until the job reaches a terminal status.
  const waitForJobCompletion = async (jobId, maxWaitMs = 12 * 60 * 1000) => {
    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
      try {
        const { data: job } = await supabase
          .from('crm_import_history')
          .select('id, imported_rows, updated_rows, failed_rows, duplicates_found, total_rows, status, errors')
          .eq('id', jobId)
          .single();
        if (job && ['completed', 'partial', 'failed'].includes(job.status)) {
          return job;
        }
      } catch { /* keep polling */ }
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
    return null;
  };

  /**
   * @param {Object} options
   * @param {string} options.functionName - 'importContacts' | 'importDeals'
   * @param {Object} options.payload - full callable payload (records, assignToId, ...)
   * @param {number} options.totalRows - payload.records.length (job discovery match)
   * @returns {Promise<Object>} results object ({ success, jobId, ... })
   */
  const runImport = useCallback(async ({ functionName, payload, totalRows }) => {
    setImporting(true);
    setImportProgress(0);
    setProgressText('Starting import...');

    try {
      const functions = getFunctions(app, 'us-east1');

      // Client timeout must exceed the 540s server limit: a shorter client
      // timeout made big imports look failed while they kept running on the
      // server, inviting a duplicate re-upload.
      const CLIENT_TIMEOUT_MS = 600000;
      const importFunction = httpsCallable(functions, functionName, { timeout: CLIENT_TIMEOUT_MS });

      // Start polling for progress before the CF returns
      const { data: latestJob } = await supabase
        .from('crm_import_history')
        .select('id')
        .eq('user_id', currentUser.uid)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      const preImportJobId = latestJob?.id;

      const resultPromise = importFunction(payload);

      // Discover the new import job for progress polling (retry a few times;
      // the server creates it within the first seconds of the call). Matching
      // on total_rows avoids latching onto an unrelated concurrent import.
      const discoverJob = async (attempt = 0) => {
        if (importJobIdRef.current) return;
        try {
          const { data: newJob } = await supabase
            .from('crm_import_history')
            .select('id')
            .eq('user_id', currentUser.uid)
            .eq('status', 'processing')
            .eq('total_rows', totalRows)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          if (newJob && newJob.id !== preImportJobId) {
            importJobIdRef.current = newJob.id;
            setImportJobId(newJob.id);
            return;
          }
        } catch { /* job may not exist yet */ }
        if (attempt < 3) setTimeout(() => discoverJob(attempt + 1), 2500);
      };
      setTimeout(() => discoverJob(), 1500);

      const result = await resultPromise;

      if (result.data.jobId) {
        importJobIdRef.current = result.data.jobId;
        setImportJobId(result.data.jobId);
      }

      if (result.data.success) {
        setImportProgress(100);
        setProgressText('Complete');
      }
      return result.data;
    } catch (error) {
      // A dropped connection / client timeout is NOT a server failure — the
      // import keeps running server-side. Recover by watching the job record
      // instead of telling the user to re-upload (which created duplicates).
      const code = String(error?.code || '');
      const genuineServerFailure = String(error?.message || '').startsWith('Import failed');
      const mayStillBeRunning = !genuineServerFailure && (
        code.includes('deadline-exceeded') || code.includes('unavailable') ||
        code.includes('internal') || code.includes('network')
      );

      if (mayStillBeRunning && importJobIdRef.current) {
        setProgressText('Connection interrupted — the import is still running on the server. Waiting for it to finish…');
        const job = await waitForJobCompletion(importJobIdRef.current);
        if (job) {
          setImportProgress(100);
          setProgressText('Complete');
          return buildResultsFromHistory(job);
        }
      }

      error.mayStillBeRunning = mayStillBeRunning;
      throw error;
    } finally {
      setImporting(false);
    }
  }, [currentUser]);

  return {
    importing,
    importProgress,
    progressText,
    importJobId,
    runImport,
    resetRunner,
  };
}
